/**
 * net/peer-manager.js — PeerJS lifecycle, lobby discovery, and the star relay.
 *
 * The GDD specifies EOS Lobby plus an EOS P2P relay (10.3). On the web there is
 * no EOS, so this substitutes the same shape on the public PeerJS broker: one
 * peer in each room owns a well-known id and relays for everyone else.
 *
 * ── Auto-lobby ──
 * There is no matchmaking service, so the lobby ids are simply agreed in
 * advance: `cc2-lobby-0`, `-1`, `-2`. Every client tries the same thing in the
 * same order — connect to lobby N as a client; if nobody answers within
 * `NET.joinTimeoutMs`, try to *claim* that id and become its host; if the claim
 * is refused (someone won the race), move to lobby N+1. A full lobby refuses
 * the joiner with `bye{reason:'full'}` and the joiner cascades too, which is
 * how the GDD's 16-goose cap turns into a second instance rather than a queue.
 *
 * ── Failure is a normal outcome ──
 * The public broker is free, shared and frequently unreachable. Every failure
 * path here ends in a resolved promise reporting `connected: false`, never a
 * rejection and never a visible error. The carnival has NPCs for exactly this.
 *
 * PeerJS itself is loaded with a dynamic import, so a page that never connects —
 * every `?test=1` run, and every offline player — never downloads it.
 *
 * @module net/peer-manager
 */

import { NET } from '../constants.js';
import { MSG, RateLimiter, unpack } from './protocol.js';

/** Room codes are uppercase alphanumerics; anything else is not a room code. */
function sanitizeRoomCode(code) {
  return String(code || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 12);
}

/** @param {string} code @returns {string} Broker id for a private room. */
export function privateRoomId(code) {
  return `${NET.idPrefix}-room-${sanitizeRoomCode(code)}`;
}

/** Strip our namespace back off a broker id for display. */
export function displayRoom(peerId) {
  if (!peerId) return null;
  const priv = `${NET.idPrefix}-room-`;
  if (peerId.startsWith(priv)) return peerId.slice(priv.length);
  return peerId.replace(`${NET.idPrefix}-`, '').toUpperCase();
}

class PeerManager {
  constructor() {
    /** @type {any} */ this.peer = null;
    /** @type {Map<string, any>} DataConnections by peer id. */
    this.connections = new Map();
    this.isHost = false;
    /** @type {string|null} The room id (host's broker id). */
    this.room = null;
    /** @type {string|null} */ this.localId = null;
    this.connected = false;

    this.limiter = new RateLimiter();
    /** @type {Array<(msg:object, from:string)=>void>} */ this._onMessage = [];
    /** @type {Array<(id:string)=>void>} */ this._onJoin = [];
    /** @type {Array<(id:string)=>void>} */ this._onLeave = [];
    /** Called when the host must refuse a joiner. */
    this.isFull = () => false;
    this._destroyed = false;
  }

  /** @param {(msg:object, from:string)=>void} fn @returns {()=>void} */
  onMessage(fn) {
    this._onMessage.push(fn);
    return () => { this._onMessage = this._onMessage.filter((f) => f !== fn); };
  }

  /** @param {(id:string)=>void} fn @returns {()=>void} */
  onPeerJoin(fn) {
    this._onJoin.push(fn);
    return () => { this._onJoin = this._onJoin.filter((f) => f !== fn); };
  }

  /** @param {(id:string)=>void} fn @returns {()=>void} */
  onPeerLeave(fn) {
    this._onLeave.push(fn);
    return () => { this._onLeave = this._onLeave.filter((f) => f !== fn); };
  }

  /**
   * Join or create a carnival.
   *
   * @param {string|null} [roomCode] A private room code, or null for auto-lobby.
   * @returns {Promise<{ connected:boolean, room:string|null, isHost:boolean,
   *                     reason?:string }>} Never rejects.
   */
  async connect(roomCode = null) {
    if (this.connected) return this.status();
    this._destroyed = false;

    let Peer;
    try {
      ({ default: Peer } = await import('peerjs'));
    } catch (err) {
      return { connected: false, room: null, isHost: false, reason: 'peerjs-unavailable' };
    }

    const candidates = roomCode
      ? [privateRoomId(roomCode)]
      : [...NET.lobbies];

    for (const roomId of candidates) {
      if (this._destroyed) break;

      const joined = await this._tryJoin(Peer, roomId);
      if (joined) return this.status();

      const hosted = await this._tryHost(Peer, roomId);
      if (hosted) return this.status();
    }

    // A private room code must never silently land the player somewhere else,
    // so there is no cascade for it — just an honest offline result.
    return { connected: false, room: null, isHost: false, reason: 'no-lobby' };
  }

  /**
   * Attempt to join `roomId` as a client.
   * @returns {Promise<boolean>}
   * @private
   */
  async _tryJoin(Peer, roomId) {
    const peer = await this._openPeer(Peer, undefined, NET.joinTimeoutMs);
    if (!peer) return false;

    const conn = await new Promise((resolve) => {
      let settled = false;
      const finish = (value) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(value);
      };
      const timer = setTimeout(() => finish(null), NET.joinTimeoutMs);

      let c;
      try {
        c = peer.connect(roomId, { reliable: true, metadata: { app: NET.idPrefix } });
      } catch {
        finish(null);
        return;
      }
      c.on('open', () => finish(c));
      c.on('error', () => finish(null));
      // PeerJS reports "peer unavailable" on the Peer, not the connection.
      peer.on('error', (err) => {
        if (err?.type === 'peer-unavailable') finish(null);
      });
    });

    if (!conn) {
      peer.destroy();
      return false;
    }

    // The host answers a joiner it cannot seat with bye{full}; wait briefly for
    // that verdict before declaring the connection good.
    const refused = await this._awaitRefusal(conn);
    if (refused) {
      try { conn.close(); } catch { /* already gone */ }
      peer.destroy();
      return false;
    }

    this.peer = peer;
    this.isHost = false;
    this.room = roomId;
    this.localId = peer.id;
    this.connected = true;
    this._bindConnection(conn);
    this._bindPeerLifecycle(peer);
    return true;
  }

  /**
   * Listen for an immediate `bye{full}` from a host that cannot seat us.
   * @returns {Promise<boolean>} True if refused.
   * @private
   */
  _awaitRefusal(conn) {
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        conn.off?.('data', onData);
        resolve(false);
      }, 900);
      const onData = (raw) => {
        const msg = unpack(raw);
        if (msg?.type === MSG.BYE && msg.reason === 'full') {
          clearTimeout(timer);
          conn.off?.('data', onData);
          resolve(true);
        }
      };
      conn.on('data', onData);
      conn.on('close', () => { clearTimeout(timer); resolve(true); });
    });
  }

  /**
   * Attempt to claim `roomId` and become its host.
   * @returns {Promise<boolean>}
   * @private
   */
  async _tryHost(Peer, roomId) {
    const peer = await this._openPeer(Peer, roomId, NET.hostClaimTimeoutMs);
    if (!peer) return false;

    this.peer = peer;
    this.isHost = true;
    this.room = roomId;
    this.localId = peer.id;
    this.connected = true;

    peer.on('connection', (conn) => {
      if (this.isFull()) {
        // Refuse politely and let them find the next lobby.
        conn.on('open', () => {
          try {
            conn.send({ v: NET.protocolVersion, t: MSG.BYE, why: 'full' });
            setTimeout(() => conn.close(), 60);
          } catch { /* nothing to do */ }
        });
        return;
      }
      this._bindConnection(conn);
    });
    this._bindPeerLifecycle(peer);
    return true;
  }

  /**
   * Construct a Peer and wait for it to open.
   * @param {any} Peer
   * @param {string|undefined} id Undefined lets the broker assign one.
   * @param {number} timeoutMs
   * @returns {Promise<any|null>}
   * @private
   */
  _openPeer(Peer, id, timeoutMs) {
    return new Promise((resolve) => {
      let peer;
      try {
        peer = id ? new Peer(id) : new Peer();
      } catch {
        resolve(null);
        return;
      }

      let settled = false;
      const finish = (value) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        if (!value) { try { peer.destroy(); } catch { /* ignore */ } }
        resolve(value);
      };
      const timer = setTimeout(() => finish(null), timeoutMs);

      peer.on('open', () => finish(peer));
      peer.on('error', () => finish(null));
    });
  }

  /** @private */
  _bindPeerLifecycle(peer) {
    peer.on('error', (err) => {
      // Losing one peer is routine on a shared broker; losing the signalling
      // server is not fatal either, because established connections survive it.
      console.warn(`[net] peer error: ${err?.type || err}`);
    });
    peer.on('disconnected', () => {
      if (!peer.destroyed && !this._destroyed) {
        try { peer.reconnect(); } catch { /* broker gone */ }
      }
    });
  }

  /** @private */
  _bindConnection(conn) {
    const id = conn.peer;

    const register = () => {
      if (this.connections.has(id)) return;
      this.connections.set(id, conn);
      for (const fn of this._onJoin) safely(fn, id);
    };

    if (conn.open) register();
    conn.on('open', register);
    conn.on('data', (raw) => this._receive(id, raw));
    conn.on('close', () => this._drop(id));
    conn.on('error', () => this._drop(id));
  }

  /** @private */
  _receive(fromId, raw) {
    // The flood guard runs before parsing: the cheapest possible drop.
    if (!this.limiter.allow(fromId)) return;

    // A host is also a relay. Forwarding happens on the raw payload with the
    // origin stamped on, so a client can tell who a message came from even
    // though it only ever talks to the host.
    if (this.isHost && raw && typeof raw === 'object') {
      const relayed = { ...raw, _from: raw._from || fromId };
      for (const [id, conn] of this.connections) {
        if (id === fromId || !conn.open) continue;
        try { conn.send(relayed); } catch { /* dropped, next pose covers it */ }
      }
    }

    const origin = (raw && typeof raw === 'object' && raw._from) || fromId;
    for (const fn of this._onMessage) safely(fn, raw, origin);
  }

  /** @private */
  _drop(id) {
    if (!this.connections.has(id)) return;
    this.connections.delete(id);
    this.limiter.forget(id);
    for (const fn of this._onLeave) safely(fn, id);
  }

  /**
   * Send to everyone. A client sends only to the host, which relays.
   * @param {object} message
   */
  broadcast(message) {
    for (const conn of this.connections.values()) {
      if (!conn.open) continue;
      try { conn.send(message); } catch { /* next tick will do */ }
      if (!this.isHost) break;
    }
  }

  /**
   * Send to one peer. From a client this still goes via the host, which relays
   * it on — so the message carries a `_to` the host honours.
   * @param {string} peerId
   * @param {object} message
   */
  sendTo(peerId, message) {
    const direct = this.connections.get(peerId);
    if (direct?.open) {
      try { direct.send(message); } catch { /* ignore */ }
      return;
    }
    this.broadcast({ ...message, _to: peerId });
  }

  /** @returns {boolean} */
  isConnected() {
    return this.connected;
  }

  /** @returns {string[]} */
  peerIds() {
    return [...this.connections.keys()];
  }

  /** @returns {{connected:boolean, room:string|null, isHost:boolean}} */
  status() {
    return { connected: this.connected, room: this.room, isHost: this.isHost };
  }

  /** Tear everything down. Safe to call when never connected. */
  disconnect() {
    this._destroyed = true;
    for (const conn of this.connections.values()) {
      try { conn.close(); } catch { /* ignore */ }
    }
    this.connections.clear();
    this.limiter.reset();
    if (this.peer && !this.peer.destroyed) {
      try { this.peer.destroy(); } catch { /* ignore */ }
    }
    this.peer = null;
    this.connected = false;
    this.isHost = false;
    this.room = null;
    this.localId = null;
  }
}

/** Run a listener without letting its exception break the network loop. */
function safely(fn, ...args) {
  try {
    fn(...args);
  } catch (err) {
    console.error('[net] listener threw:', err);
    if (Array.isArray(window.__ccErrors)) window.__ccErrors.push(`net: ${err.message}`);
  }
}

/** The one peer manager. */
export const peerManager = new PeerManager();
